<template>
  <sn-shadow
    class="sn-task-card"
    :shadow-depth="expanded ? 2 : 1"
    :shadow-hover-depth="expandable ? 2 : 1"
    :style="`width: ${width}`"
  >
    <component
      :is="includeExpansionPanelsComponent ? 'sn-expansion-panels' : 'div'"
      :expandable="expandable"
    >
      <sn-expansion-panel
        :expandable="expandable"
        :external-load-icon="externalLoadIcon"
        :hide-chevron-deprecated-borrower-team-only="hideChevronDeprecatedBorrowerTeamOnly"
        @click="handleClick"
      >
        <template v-slot:header>
          <v-row
            no-gutters
            align="center"
            justify="center"
            class="pa-4"
            :data-cy="title + '-task-card'"
          >
            <v-col cols="auto">
              <v-icon
                v-if="getIcon"
                :class="`icon-background ${iconClass}`"
                data-cy="task-icon"
              >
                {{ getIcon }}
              </v-icon>
              <sn-illustration
                v-if="illustration"
                :illustration="illustration"
                width="70"
                height="auto"
              />
            </v-col>
            <v-col class="ml-2">
              <div
                class="sn-h5 sn-text-primary-03"
                data-cy="task-title"
              >
                {{ title }}
              </div>
              <div
                class="sn-body-2 sn-text-primary-01"
                data-cy="task-subtitle"
                v-html="getSubtitle"
              />
            </v-col>
            <v-col cols="auto">
              <div
                class="sn-body-1 sn-text-primary-03 flex"
                data-cy="task-arrow-text"
              >
                <slot name="arrow-text-component" /> {{ arrowText }}
              </div>
            </v-col>
          </v-row>
        </template>
        <template v-slot:expansion>
          <div
            :class="externalLoadIcon ? '' : 'px-8 pt-2'"
          >
            <slot />
          </div>
          <div
            v-if="showFooter"
            class="sn-task-card-footer mt-6"
            :style="getFooterStyle"
          >
            <v-container
              fluid
            >
              <v-row
                justify="end"
                align="center"
                class="pr-3"
              >
                <v-col
                  v-if="showDismiss"
                  cols="auto"
                >
                  <sn-button
                    type="text"
                    size="large"
                    :text="dismissButtonText"
                    :loading="dismissButtonLoading"
                    :disabled="dismissButtonDisabled"
                    data-cy="task-dismiss-btn"
                    @click="$emit('dismiss')"
                  />
                </v-col>
                <v-col
                  v-if="showSuccess"
                  cols="auto"
                >
                  <sn-button
                    type="primary"
                    size="large"
                    :text="successButtonText"
                    :loading="successButtonLoading"
                    :disabled="successButtonDisabled"
                    data-cy="task-success-btn"
                    @click="$emit('success')"
                  />
                </v-col>
              </v-row>
            </v-container>
          </div>
        </template>
      </sn-expansion-panel>
    </component>
  </sn-shadow>
</template>
<script>

import snShadow from '~/components/snui/v2/snShadow/snShadow'
import snIllustration from '../snIllustration/snIllustration'
import snExpansionPanel from '../snExpansionPanel/snExpansionPanel'
import snExpansionPanels from '../snExpansionPanel/snExpansionPanels'
import snButton from '../snBtn/snBtn'

export default {
  name: 'SnTaskCard',

  components: {
    snExpansionPanel,
    snExpansionPanels,
    snButton,
    snShadow,
    snIllustration
  },

  props: {
    width: {
      type: String,
      required: false,
      default: 'auto'
    },
    title: {
      type: String,
      required: false,
      default: ''
    },
    subtitle: {
      type: String,
      required: false,
      default: ''
    },
    arrowText: {
      type: String,
      required: false,
      default: ''
    },
    icon: {
      type: String,
      required: false,
      default: ''
    },
    illustration: {
      type: String,
      required: false,
      default: ''
    },
    status: {
      type: String,
      required: false,
      default: ''
    },
    dismissButtonText: {
      type: String,
      required: false,
      default: 'Dismiss'
    },
    dismissButtonLoading: {
      type: Boolean,
      required: false,
      default: false
    },
    dismissButtonDisabled: {
      type: Boolean,
      required: false,
      default: false
    },
    successButtonText: {
      type: String,
      required: false,
      default: 'Label'
    },
    successButtonLoading: {
      type: Boolean,
      required: false,
      default: false
    },
    successButtonDisabled: {
      type: Boolean,
      required: false,
      default: false
    },
    showFooter: {
      type: Boolean,
      required: false,
      default: true
    },
    expandable: {
      type: Boolean,
      required: false,
      default: true
    },
    showDismiss: {
      type: Boolean,
      required: false,
      default: true
    },
    showSuccess: {
      type: Boolean,
      required: false,
      default: true
    },
    hideChevronDeprecatedBorrowerTeamOnly: {
      type: Boolean,
      required: false,
      default: false
    },
    includeExpansionPanelsComponent: {
      type: Boolean,
      required: false,
      default: true
    },
    externalLoadIcon: {
      type: Boolean,
      required: false,
      default: false
    },
    expanded: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    getIcon () {
      return this.calculateStatus?.statusIcon ?? this.icon
    },
    iconClass () {
      return this.calculateStatus.class
    },
    getSubtitle () {
      return this.calculateStatus?.subtitle ?? this.subtitle
    },
    getFooterStyle () {
      return {
        background: this.$vuetify.theme.themes.light.snBrand01
      }
    },
    calculateStatus () {
      const subtitle = this.subtitle
      if (!this.status) {
        return {
          class: 'normal-icon'
        }
      }
      switch (this.status.toLowerCase()) {
        case 'completed':
          return {
            subtitle: subtitle,
            statusIcon: '$snCheck',
            class: 'success-icon'
          }
        case 'urgent':
          return {
            subtitle: `<div><span class='snCaution03--text sn-font-weight-bold'>Urgent: </span>${subtitle}</div>`,
            class: 'urgent-icon'
          }
        case 'pending':
          return {
            subtitle: `<div><span class='snPrimary01--text sn-font-weight-bold'>Pending: </span>${subtitle}</div>`,
            statusIcon: '$snTimeArrow',
            class: 'pending-icon'
          }
        case 'rejected':
          return {
            subtitle: `<div><span class='snError03--text sn-font-weight-bold'>Rejected: </span>${subtitle}</div>`,
            statusIcon: '$snExit',
            class: 'rejected-icon'
          }
        default:
          return {
            class: 'normal-icon'
          }
      }
    }
  },
  methods: {
    handleClick (event) {
      this.$emit('click', event)
    }
  }
}
</script>

<style scoped lang="scss">
@import './app/javascript/assets/css/snui';

.vuetifycss .sn-task-card,
.sn-task-card {
  margin: auto;
  width: var(--content-width);
  max-width: var(--content-width);
  border-radius: 8px;
}
.sn-task-card-footer{
  height: 96px;
  border-radius: 0 0 8px 8px;
}
.icon-background {
  border-radius: 300px;
  height: 40px;
  width: 40px;
  padding: 7px;
}
.success-icon {
  color: $sn-success-03;
  background-color: $sn-success-01
}
.pending-icon {
  color: $sn-primary-03;
  background-color: $sn-gray-01
}
.urgent-icon {
  color: $sn-caution-03;
  background-color: $sn-caution-01
}
.rejected-icon {
  color: $sn-error-03;
  background-color: $sn-error-01
}
.normal-icon {
  color: $sn-primary-03;
  background-color: $sn-gray-01
}
.flex {
  display: flex;
  align-items: center;
}
</style>
